import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import {
  McpToken,
  CreateMcpTokenPayload,
  CreateMcpTokenResponse,
  ApiKey,
  CreateApiKeyPayload,
  CreateApiKeyResponse,
  DeleteApiKeyResponse,
  PaginatedResponse,
} from './types';

// -------------------------------------------------------------
// MCP Client Tokens
// -------------------------------------------------------------

export function useMcpTokens() {
  return useQuery<McpToken[]>({
    queryKey: ['mcp-tokens'],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<McpToken> | McpToken[]>('/v2/mcp-tokens/');
      if (Array.isArray(res.data)) return res.data;
      return res.data?.results || [];
    },
    retry: false, // Do not spam retries if backend service is offline
    staleTime: 30 * 1000,
  });
}

export function useCreateMcpToken() {
  const queryClient = useQueryClient();

  return useMutation<CreateMcpTokenResponse, Error, CreateMcpTokenPayload>({
    mutationFn: async (payload) => {
      const res = await api.post<CreateMcpTokenResponse>('/v2/mcp-tokens/', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcp-tokens'] });
    },
  });
}

export function useDeleteMcpToken() {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: async (tokenId) => {
      await api.delete(`/v2/mcp-tokens/${tokenId}/`);
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcp-tokens'] });
    },
  });
}

// -------------------------------------------------------------
// Developer API Keys (JWT)
// -------------------------------------------------------------

export function useApiKeys() {
  return useQuery<ApiKey[]>({
    queryKey: ['api-keys'],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<ApiKey> | ApiKey[]>('/v2/api-keys/');
      if (Array.isArray(res.data)) return res.data;
      return res.data?.results || [];
    },
    retry: false, // Do not spam retries if backend service is offline
    staleTime: 30 * 1000,
  });
}

export function useCreateApiKey() {
  const queryClient = useQueryClient();

  return useMutation<CreateApiKeyResponse, Error, CreateApiKeyPayload>({
    mutationFn: async (payload) => {
      const res = await api.post<CreateApiKeyResponse>('/v2/api-keys/', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });
}

export function useDeleteApiKey() {
  const queryClient = useQueryClient();

  return useMutation<DeleteApiKeyResponse, Error, string>({
    mutationFn: async (keyId) => {
      const res = await api.delete<DeleteApiKeyResponse>(`/v2/api-keys/${keyId}/`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });
}
